import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  applyNodeChanges,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import SystemNodeComponent, { type SystemNodeData, type SystemNode } from './SystemNode';
import ConnectionEdgeComponent, { type ConnectionEdge } from './ConnectionEdge';
import FloatingSignaturesPanel from './FloatingSignaturesPanel';
import { useMapStore } from '../../store/mapStore';
import { useAuthStore } from '../../store/authStore';
import { getStaticsForSystem } from '../../lib/wormholeData';
import { applyDagreLayout, applyLayoutPreservingLocked } from '../../lib/layout';
import { apiUrl } from '../../lib/config';
import { useAutoMapping } from '../../hooks/useAutoMapping';
import { useWebSocket } from '../../hooks/useWebSocket';

// Context menu state
interface ContextMenuState {
  x: number;
  y: number;
  flowX: number;
  flowY: number;
}

// Register custom node and edge types
const nodeTypes = {
  system: SystemNodeComponent,
};

const edgeTypes = {
  connection: ConnectionEdgeComponent,
};

function MapCanvasInner() {
  const {
    currentMap,
    systems,
    connections,
    presence,
    selectedSystemId,
    selectedConnectionId,
    selectSystem,
    selectConnection,
    updateSystem,
    addConnection,
    removeConnection,
    removeSystem,
    addSystem,
  } = useMapStore();

  const { character } = useAuthStore();
  const { screenToFlowPosition } = useReactFlow();

  // Auto-mapping state (must be before useAutoMapping hook)
  const [autoMappingEnabled, setAutoMappingEnabled] = useState(true);

  // WebSocket for real-time collaboration (must be before useAutoMapping to provide sendAction)
  const { status: wsStatus, sendAction } = useWebSocket({
    mapId: currentMap?.id || '',
    onError: (error) => console.error('WebSocket error:', error),
  });

  // Auto-mapping: automatically add systems and connections when jumping
  const { currentSystemId, isActive: isAutoMappingActive } = useAutoMapping({
    enabled: autoMappingEnabled,
    pollingInterval: 5000,
    sendAction,
  });

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [showAddSystemModal, setShowAddSystemModal] = useState(false);
  const [addSystemPosition, setAddSystemPosition] = useState({ x: 0, y: 0 });
  const [systemSearchQuery, setSystemSearchQuery] = useState('');
  const [systemSearchResults, setSystemSearchResults] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedSystem, setSelectedSystem] = useState<{ id: number; name: string } | null>(null);
  const [systemAliasInput, setSystemAliasInput] = useState('');
  const [addingSystem, setAddingSystem] = useState(false);
  const [addSystemError, setAddSystemError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Convert store data to React Flow nodes
  const initialNodes: SystemNode[] = useMemo(() => {
    return systems.map((system): SystemNode => {
      // Use backend statics if available, otherwise fall back to lookup table
      const statics = system.statics || getStaticsForSystem(system.systemId) || undefined;

      // Get pilots in this system from presence data
      const pilotsInSystem = presence
        .filter((p) => p.systemId === system.systemId)
        .map((p) => ({
          characterId: p.characterId,
          characterName: p.characterName,
          isOnline: p.isOnline,
        }));

      // Add current user if they're in this system (based on location polling)
      if (character && currentSystemId === system.systemId) {
        // Check if current user is already in the list
        const alreadyPresent = pilotsInSystem.some((p) => p.characterId === character.id);
        if (!alreadyPresent) {
          pilotsInSystem.unshift({
            characterId: character.id,
            characterName: character.name,
            isOnline: true,
          });
        }
      }

      return {
        id: system.id,
        type: 'system',
        position: { x: system.positionX, y: system.positionY },
        data: {
          id: system.id,
          systemId: system.systemId,
          systemName: system.systemName,
          alias: system.alias,
          securityStatus: system.securityStatus,
          wormholeClass: system.wormholeClass,
          effect: system.effect,
          locked: system.locked,
          statics,
          pilots: pilotsInSystem,
        },
        selected: system.id === selectedSystemId,
      };
    });
  }, [systems, selectedSystemId, presence, character, currentSystemId]);

  // Convert store data to React Flow edges
  const initialEdges: ConnectionEdge[] = useMemo(() => {
    // Determine best handles based on relative node positions
    const getBestHandles = (
      sourceId: string,
      targetId: string
    ): { sourceHandle: string; targetHandle: string } => {
      const sourceSystem = systems.find((s) => s.id === sourceId);
      const targetSystem = systems.find((s) => s.id === targetId);

      if (!sourceSystem || !targetSystem) {
        return { sourceHandle: 'bottom', targetHandle: 'top' };
      }

      const dx = targetSystem.positionX - sourceSystem.positionX;
      const dy = targetSystem.positionY - sourceSystem.positionY;

      // Use vertical handles if vertical distance >= horizontal, otherwise horizontal
      if (Math.abs(dy) >= Math.abs(dx)) {
        // Vertical: source above target -> bottom to top, otherwise top to bottom
        if (dy >= 0) {
          return { sourceHandle: 'bottom', targetHandle: 'top' };
        } else {
          return { sourceHandle: 'top-source', targetHandle: 'bottom-target' };
        }
      } else {
        // Horizontal: source left of target -> right to left, otherwise left to right
        if (dx >= 0) {
          return { sourceHandle: 'right', targetHandle: 'left' };
        } else {
          return { sourceHandle: 'left-source', targetHandle: 'right-target' };
        }
      }
    };

    return connections.map((conn): ConnectionEdge => {
      const { sourceHandle, targetHandle } = getBestHandles(
        conn.sourceSystemId,
        conn.targetSystemId
      );
      return {
        id: conn.id,
        source: conn.sourceSystemId,
        target: conn.targetSystemId,
        sourceHandle,
        targetHandle,
        type: 'connection',
        data: {
          id: conn.id,
          wormholeType: conn.wormholeType,
          massStatus: conn.massStatus,
          timeStatus: conn.timeStatus,
          sourceSig: conn.sourceSig,
          targetSig: conn.targetSig,
          eolAt: conn.eolAt,
        },
        selected: conn.id === selectedConnectionId,
      };
    });
  }, [connections, selectedConnectionId, systems]);

  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);
  const [autoLayout, setAutoLayout] = useState(false);

  // Sync nodes when store changes
  useEffect(() => {
    if (autoLayout) {
      const layoutedNodes = applyLayoutPreservingLocked(
        initialNodes,
        initialEdges,
        (nodes, edges) => applyDagreLayout(nodes, edges, { direction: 'TB' })
      );
      setNodes(layoutedNodes as SystemNode[]);
    } else {
      setNodes(initialNodes);
    }
  }, [initialNodes, autoLayout, setNodes, initialEdges]);

  // Sync edges when store changes
  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Handle node changes (position, selection, removal)
  const onNodesChange = useCallback(
    async (changes: NodeChange[]) => {
      // Apply changes locally first for responsive UI
      setNodes((nds) => applyNodeChanges(changes, nds) as SystemNode[]);

      // Handle deletions - call removeSystem for each removed node
      for (const change of changes) {
        if (change.type === 'remove') {
          const success = await removeSystem(change.id);
          if (success) {
            // Broadcast to other connected clients via WebSocket
            sendAction('REMOVE_SYSTEM', { id: change.id });
          }
        }
      }

      // Persist position changes to server
      for (const change of changes) {
        if (change.type === 'position' && change.position && change.dragging === false) {
          // Only save when drag ends
          const success = await updateSystem(change.id, {
            positionX: Math.round(change.position.x),
            positionY: Math.round(change.position.y),
          });
          if (success) {
            // Broadcast position update to other clients
            sendAction('UPDATE_SYSTEM', {
              id: change.id,
              positionX: Math.round(change.position.x),
              positionY: Math.round(change.position.y),
            });
          }
        }
      }
    },
    [setNodes, updateSystem, removeSystem, sendAction]
  );

  // Handle node selection
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      selectSystem(node.id);
    },
    [selectSystem]
  );

  // Handle edge selection
  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      selectConnection(edge.id);
    },
    [selectConnection]
  );

  // Handle background click (deselect)
  const onPaneClick = useCallback(() => {
    selectSystem(null);
    selectConnection(null);
  }, [selectSystem, selectConnection]);

  // Handle new connection creation
  const onConnect = useCallback(
    async (connection: Connection) => {
      if (connection.source && connection.target) {
        const result = await addConnection(connection.source, connection.target);
        if (result) {
          // Broadcast to other connected clients via WebSocket
          sendAction('ADD_CONNECTION', result);
        }
      }
    },
    [addConnection, sendAction]
  );

  // Handle edge deletion (via keyboard)
  const onEdgesDelete = useCallback(
    async (edges: Edge[]) => {
      for (const edge of edges) {
        const success = await removeConnection(edge.id);
        if (success) {
          // Broadcast to other connected clients via WebSocket
          sendAction('REMOVE_CONNECTION', { id: edge.id });
        }
      }
    },
    [removeConnection, sendAction]
  );

  // Handle node deletion (via keyboard)
  const onNodesDelete = useCallback(
    async (deletedNodes: Node[]) => {
      for (const node of deletedNodes) {
        const success = await removeSystem(node.id);
        if (success) {
          // Broadcast to other connected clients via WebSocket
          sendAction('REMOVE_SYSTEM', { id: node.id });
        }
      }
    },
    [removeSystem, sendAction]
  );

  // Apply automatic layout
  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = applyDagreLayout(nodes, edges, {
      direction: 'TB',
    });
    setNodes(layoutedNodes as SystemNode[]);

    // Save new positions to server
    layoutedNodes.forEach((node) => {
      if (!node.data?.locked) {
        updateSystem(node.id, {
          positionX: Math.round(node.position.x),
          positionY: Math.round(node.position.y),
        });
      }
    });
  }, [nodes, edges, setNodes, updateSystem]);

  // Handle right-click context menu
  const onPaneContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      event.preventDefault();
      const flowPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        flowX: flowPosition.x,
        flowY: flowPosition.y,
      });
    },
    [screenToFlowPosition]
  );

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Open add system modal
  const openAddSystemModal = useCallback(() => {
    if (contextMenu) {
      setAddSystemPosition({ x: contextMenu.flowX, y: contextMenu.flowY });
    }
    setContextMenu(null);
    setShowAddSystemModal(true);
    setSystemSearchQuery('');
    setSystemSearchResults([]);
    setSelectedSystem(null);
    setSystemAliasInput('');
    setAddSystemError(null);
  }, [contextMenu]);

  // Debounced system search
  useEffect(() => {
    if (!showAddSystemModal || systemSearchQuery.length < 2) {
      setSystemSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(apiUrl(`/api/esi/systems/search?q=${encodeURIComponent(systemSearchQuery)}`), {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setSystemSearchResults(data.systems || []);
        }
      } catch (error) {
        console.error('System search failed:', error);
      }
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [systemSearchQuery, showAddSystemModal]);

  // Handle add system
  const handleAddSystem = useCallback(async () => {
    if (!selectedSystem) {
      setAddSystemError('Please select a system from the search results');
      return;
    }

    setAddingSystem(true);
    setAddSystemError(null);

    const result = await addSystem(
      selectedSystem.id,
      systemAliasInput.trim() || undefined,
      Math.round(addSystemPosition.x),
      Math.round(addSystemPosition.y)
    );

    if (result) {
      // Broadcast to other connected clients via WebSocket
      sendAction('ADD_SYSTEM', result);

      setShowAddSystemModal(false);
      setSystemSearchQuery('');
      setSystemSearchResults([]);
      setSelectedSystem(null);
      setSystemAliasInput('');
    } else {
      setAddSystemError('Failed to add system. It may already exist on this map.');
    }
    setAddingSystem(false);
  }, [selectedSystem, systemAliasInput, addSystemPosition, addSystem, sendAction]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = () => closeContextMenu();
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, closeContextMenu]);

  // Get selected system details for the signatures panel
  const selectedSystemData = useMemo(() => {
    if (!selectedSystemId) return null;
    return systems.find((s) => s.id === selectedSystemId) || null;
  }, [systems, selectedSystemId]);

  // MiniMap node color based on wormhole class
  const miniMapNodeColor = useCallback((node: Node) => {
    const data = node.data as SystemNodeData | undefined;
    const wormholeClass = data?.wormholeClass;
    const securityStatus = data?.securityStatus;

    if (wormholeClass) {
      const classColors: Record<string, string> = {
        C1: '#3b82f6',
        C2: '#60a5fa',
        C3: '#06b6d4',
        C4: '#14b8a6',
        C5: '#f97316',
        C6: '#ef4444',
        C13: '#a855f7',
        Thera: '#8b5cf6',
      };
      return classColors[wormholeClass] || '#6b7280';
    }

    if (securityStatus !== null && securityStatus !== undefined) {
      if (securityStatus >= 0.5) return '#22c55e';
      if (securityStatus > 0) return '#eab308';
      return '#ef4444';
    }

    return '#6b7280';
  }, []);

  if (!currentMap) {
    return (
      <div className="flex h-full items-center justify-center text-gray-400">
        No map loaded
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        deleteKeyCode={['Backspace', 'Delete']}
        className="bg-gray-900"
      >
        <Background color="#374151" gap={20} />
        <Controls className="!bg-gray-800 !border-gray-700" />
        <MiniMap
          nodeColor={miniMapNodeColor}
          maskColor="rgba(0, 0, 0, 0.8)"
          className="!bg-gray-800 !border-gray-700"
        />

        {/* Controls panel */}
        <Panel position="top-right" className="flex gap-2">
          <button
            onClick={handleAutoLayout}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition"
          >
            Auto Layout
          </button>
          <label className="flex items-center gap-2 rounded bg-gray-800 px-3 py-1.5 text-sm text-white">
            <input
              type="checkbox"
              checked={autoLayout}
              onChange={(e) => setAutoLayout(e.target.checked)}
              className="rounded"
            />
            Auto-arrange
          </label>
          <label className="flex items-center gap-2 rounded bg-gray-800 px-3 py-1.5 text-sm text-white" title="Automatically add systems and connections when you jump">
            <input
              type="checkbox"
              checked={autoMappingEnabled}
              onChange={(e) => setAutoMappingEnabled(e.target.checked)}
              className="rounded"
            />
            <span className={isAutoMappingActive ? 'text-green-400' : ''}>
              Auto-map
            </span>
            {isAutoMappingActive && currentSystemId && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-400 animate-pulse" title={`Tracking system ${currentSystemId}`} />
            )}
          </label>
        </Panel>

        {/* Map info panel */}
        <Panel position="top-left" className="rounded bg-gray-800/90 px-3 py-2">
          <div className="text-sm text-white font-semibold">{currentMap.name}</div>
          <div className="text-xs text-gray-400">
            {systems.length} systems · {connections.length} connections
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className={`h-2 w-2 rounded-full ${
                wsStatus === 'connected'
                  ? 'bg-green-500'
                  : wsStatus === 'connecting'
                  ? 'bg-yellow-500 animate-pulse'
                  : 'bg-red-500'
              }`}
              title={`WebSocket: ${wsStatus}`}
            />
            <span className="text-xs text-gray-500">
              {wsStatus === 'connected' ? 'Live' : wsStatus === 'connecting' ? 'Connecting...' : 'Offline'}
            </span>
          </div>
        </Panel>
      </ReactFlow>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 rounded-lg bg-gray-800 border border-gray-700 shadow-xl py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={openAddSystemModal}
            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add System
          </button>
        </div>
      )}

      {/* Add System Modal */}
      {showAddSystemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-gray-800 p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Add System</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddSystem();
              }}
            >
              <div className="mb-4">
                <label className="mb-2 block text-sm text-gray-400">
                  System Name or ID
                  <span className="ml-1 text-xs text-gray-500">(exact name or system ID)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedSystem ? selectedSystem.name : systemSearchQuery}
                    onChange={(e) => {
                      setSystemSearchQuery(e.target.value);
                      setSelectedSystem(null);
                    }}
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Jita, Amarr, or 31000001..."
                    autoFocus
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  )}
                  {/* Search results dropdown */}
                  {systemSearchResults.length > 0 && !selectedSystem && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-600 bg-gray-700 shadow-lg max-h-48 overflow-y-auto">
                      {systemSearchResults.map((system) => (
                        <button
                          key={system.id}
                          type="button"
                          onClick={() => {
                            setSelectedSystem(system);
                            setSystemSearchResults([]);
                          }}
                          className="w-full px-3 py-2 text-left text-white hover:bg-gray-600 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {system.name}
                          <span className="ml-2 text-xs text-gray-400">({system.id})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {systemSearchQuery.length >= 2 && systemSearchResults.length === 0 && !isSearching && !selectedSystem && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-400">
                      No match. Try exact name (Jita) or system ID (30000142)
                    </div>
                  )}
                </div>
                {selectedSystem && (
                  <div className="mt-2 text-xs text-green-400">
                    Selected: {selectedSystem.name} (ID: {selectedSystem.id})
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm text-gray-400">
                  Alias (optional)
                  <span className="ml-1 text-xs text-gray-500">(e.g., "Home", "Static C3")</span>
                </label>
                <input
                  type="text"
                  value={systemAliasInput}
                  onChange={(e) => setSystemAliasInput(e.target.value)}
                  className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="Enter alias"
                />
              </div>
              {addSystemError && (
                <div className="mb-4 text-sm text-red-400">{addSystemError}</div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddSystemModal(false)}
                  className="flex-1 rounded-lg bg-gray-700 px-4 py-2 text-white transition hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingSystem || !selectedSystem}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {addingSystem ? 'Adding...' : 'Add System'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Floating Signatures Panel - always visible */}
      <FloatingSignaturesPanel
        systemId={selectedSystemData?.id}
        systemName={selectedSystemData?.systemName}
        wormholeClass={selectedSystemData?.wormholeClass}
      />
    </div>
  );
}

// Wrapper component that provides ReactFlow context
export default function MapCanvas() {
  return (
    <ReactFlowProvider>
      <MapCanvasInner />
    </ReactFlowProvider>
  );
}
